import { useState } from 'react';
import { fetchApiData, interpolate } from '../Utils/ApiCalls';
import { DOWNLOAD_USAGE_SUMMARY_GG_ENDPOINT, DOWNLOAD_USAGE_SUMMARY_USL, USAGE_SUMMARY_ENDPOINT } from '../urls';
import useFormStatus from '../Hooks/useFormStatus';
import { Alert } from './Alert';
import { COMPANY_ENUM } from '../Constants/Constants';
import { Button } from '@attentive-platform/stem-ui';

const USAGE = {
  DEFAULT: 1,
  BPT: 2
};

const DownloadUsageSummary = ({ company, isBlueprintSummary, downloadBothUsage }) => {
  const initDates = {
    startDate: '',
    endDate: '',
    minEndDate: '',
    maxEndDate: ''
  };

  const [userEmail, setUserEmail] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [loading, setLoading] = useState({
    [USAGE.DEFAULT]: false,
    [USAGE.BPT]: false
  });
  const [dates, setDates] = useState(initDates);

  const { errorMessage, successMessage, handleApiResult } = useFormStatus();

  const isUsl = company === COMPANY_ENUM.USL;
  const isRexel = company === COMPANY_ENUM.REXEL;
  const generalUsage = !company;

  const handleSubmit = async (e, mode) => {
    e.preventDefault();

    const data = {
      start_date: dates.startDate,
      end_date: dates.endDate
    };
    if (generalUsage || isRexel) data.email = userEmail.toLowerCase();

    setLoading(prev => ({ ...prev, [mode]: true }));
    const url =
      generalUsage || isRexel
        ? USAGE_SUMMARY_ENDPOINT
        : isUsl
          ? interpolate(DOWNLOAD_USAGE_SUMMARY_USL, [companyId])
          : DOWNLOAD_USAGE_SUMMARY_GG_ENDPOINT;
    const apiData = {
      endpoint: url
    };

    if (isBlueprintSummary) {
      data.is_aerial_report_required = false;
      apiData.params = data;
    }

    if (mode === USAGE.BPT) {
      data.is_beam_report_required = true;
    }

    if (isUsl) {
      apiData.params = data;
      apiData.apiRequest = 'GET';
    } else {
      if (isRexel) apiData.params = { origin: 2 };
      apiData.data = data;
      apiData.apiRequest = 'POST';
    }
    const res = await fetchApiData(apiData);
    handleApiResult(res);
    setLoading(prev => ({ ...prev, [mode]: false }));
    if (res?.data) {
      isUsl && setCompanyId('');
      setDates(initDates);
      let resData;
      if (!isUsl || generalUsage) {
        resData = res?.data?.url;
      } else {
        resData = res?.data?.download;
      }
      window.open(resData, '_blank');
    }
  };

  const handleDateChange = e => {
    const date = e.target.value;
    // Min End Date should be equal or more than start date
    setDates({
      ...dates,
      startDate: date,
      minEndDate: date
    });
  };

  return (
    <form {...(!downloadBothUsage && { onSubmit: e => handleSubmit(e, USAGE.DEFAULT) })}>
      <Alert errorMessage={errorMessage} successMessage={successMessage} />
      {(generalUsage || isRexel) && (
        <div className='input-group mb-3 input-group-lg'>
          <span className='input-group-text'> User Email</span>
          <input
            className='form-control'
            type='email'
            value={userEmail}
            onChange={e => setUserEmail(e.target.value)}
            placeholder='User Email'
            required
          />
        </div>
      )}
      {isUsl && (
        <div className='input-group mb-3 input-group-lg'>
          <span className='input-group-text'> Company ID(uuid)</span>
          <input
            className='form-control'
            type='text'
            value={companyId}
            onChange={e => setCompanyId(e.target.value)}
            placeholder='uuid'
            required
          />
        </div>
      )}
      <div className='input-group input-group-lg'>
        <span className='m-2'>Completed at:</span>
      </div>
      <div className="input-group input-group-lg">
        <span className="input-group-text">Start Date</span>
        <input
          className='form-control'
          type='date'
          value={dates.startDate}
          onChange={e => handleDateChange(e)}
          min='2023-01-01T00:00'
          max='2040-12-31T23:59'
        />
      </div>
      <div className="input-group mt-3 input-group-lg w-100">
        <span className="input-group-text">End Date</span>
        <input
          className='form-control'
          type='date'
          value={dates.endDate}
          onChange={e => setDates({ ...dates, endDate: e.target.value })}
          min={dates.minEndDate}
          max='2040-12-31T23:59'
          disabled={dates.startDate === ''}
        />
      </div>
      <div className='flex justify-center align-center gap-8 mt-8'>
        {downloadBothUsage ? (
          <>
            <Button
              color='primary'
              size='large'
              variant='contained'
              onClick={e => handleSubmit(e, USAGE.DEFAULT)}
              loading={loading[USAGE.DEFAULT]}>
              Download aerial usage
            </Button>

            <Button
              color='primary'
              size='large'
              variant='contained'
              onClick={e => handleSubmit(e, USAGE.BPT)}
              loading={loading[USAGE.BPT]}>
              Download construction usage
            </Button>
          </>
        ) : (
          <Button
            color='primary'
            size='large'
            variant='contained'
            onClick={e => handleSubmit(e, USAGE.DEFAULT)}
            type='submit'
            loading={loading[USAGE.DEFAULT]}>
            Download
          </Button>
        )}
      </div>
    </form>
  );
};

export default DownloadUsageSummary;
